import { ArrowLeft, ArrowRight, Info, Smartphone, User } from 'lucide-react';
import React, { FC, ReactNode } from 'react';

import Field from '~components/ui/Field';
import Form from '~components/ui/Form';
import Input from '~components/ui/Input/Input';
import Loading from '~components/ui/Loading';
import { postToAPI } from '~utils/api/common';

export interface PersonalInfo {
  name: string;
  lastName: string;
  phone: string;
}

interface Props {
  onBack: () => void;
  onContinue: (data: PersonalInfo) => void;
  defaultValues?: Partial<PersonalInfo>;
  smsEndpoint?: string;
  subtitle?: ReactNode;
  infoText?: ReactNode;
}

const PersonalInfoForm: FC<Props> = ({
  onContinue,
  onBack,
  defaultValues,
  smsEndpoint = '/uas-students/send-sms-code',
  subtitle = 'Completa tu información para registrarte en el programa de beneficios',
  infoText = 'Tu número de teléfono verificado te permitirá acceder a los beneficios de Citicinemas que la Universidad Autonoma de Sinaloa ofrece a sus estudiantes.',
}) => {
  const [loading, setLoading] = React.useState(false);
  const form = Form.useForm<PersonalInfo>({ defaultValues });
  const handleSubmit = (data: PersonalInfo) => {
    setLoading(true);
    postToAPI(smsEndpoint, data)
      .then(() => {
        onContinue(data);
      })
      .finally(() => setLoading(false));
  };
  return (
    <Loading show={loading} size="normal" label="Validando información">
      <div className="flex flex-col items-center gap-6 py-8 px-6 md:px-8">
        <span className="rounded-full bg-primary/5 p-4 text-primary">
          <User size={32} />
        </span>
        <div>
          <h1 className="h3 font-bold text-center">Datos Personales</h1>
          <p className="text-support">{subtitle}</p>
        </div>
        <Form form={form} onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
          <div className="grid grid-cols-2 gap-6">
            <Field
              name="name"
              label={
                <span className="flex items-center gap-2">
                  <User />
                  <span>Nombre</span>
                </span>
              }
              rules={[{ required: true, message: 'Nombre obligatorio' }]}
            >
              <Input placeholder="Juan" />
            </Field>
            <Field
              name="lastName"
              label={
                <span className="flex items-center gap-2">
                  <User />
                  <span>Apellido</span>
                </span>
              }
              rules={[{ required: true, message: 'Apellido obligatorio' }]}
            >
              <Input placeholder="Pérez" />
            </Field>
          </div>
          <Field
            name="phone"
            label={
              <span className="flex items-center gap-2">
                <Smartphone />
                <span>Celular</span>
              </span>
            }
            help="Se enviará un código de verificación por SMS para confirmar tu número"
            rules={{ required: true, message: 'Ingresa tu número de celular (10 dígitos)' }}
          >
            <Input type="number" maxLength={10} />
          </Field>
          <p className=" bg-info/10 p-4 border border-info/20 rounded-lg flex gap-2">
            <span>
              <Info size={24} className="text-info" />
            </span>
            <span>{infoText}</span>
          </p>
          <div className="grid grid-cols-2 gap-6">
            <button className="btn dark:btn-dark" type="button" onClick={() => onBack()}>
              <ArrowLeft size={16} /> <span>Regresar</span>
            </button>
            <button className="btn btn-primary" type="submit">
              <span>Continuar</span> <ArrowRight size={16} />
            </button>
          </div>
        </Form>
      </div>
    </Loading>
  );
};

export default PersonalInfoForm;
